<script>
import Head from '@/components/Head';
import Button from '@/components/Button';
import Loader from '@/components/Loader';
import { toast } from 'vue3-toastify';
import 'vue3-toastify/dist/index.css';

export default {
    data() {
        return {
            loader: false,
            duplicates: [],
            selectedUser: null,
            pendingInstallments: [],
            showLinkModal: false,
            selectedPayment: null,
            selectedInstallments: [], // Array para múltiplas seleções
        }
    },
    components: {
        Head,
        Button,
        Loader,
    },
    created() {
        this.loadDuplicatePayments();
    },
    methods: {
        notify(text, type) {
            toast(text, {
                "type": type == 'info' ? 'info' : type == 'warning' ? 'warning' : type == 'error' ? 'error' : type == 'success' ? 'success' : 'default',
            });
        },
        
        loadDuplicatePayments() {
            this.loader = true;

            this.$axios.get('/payments/duplicates/list')
            .then(res => {
                this.duplicates = res.data.data.duplicates || [];
                this.loader = false;
                
                if (this.duplicates.length === 0) {
                    this.notify('Nenhum pagamento duplicado encontrado!', 'info');
                }
            })
            .catch(err => {
                this.notify('Erro ao carregar pagamentos duplicados!', 'error');
                this.loader = false;
            });
        },

        async loadPendingInstallments(userId) {
            try {
                const res = await this.$axios.get(`/payments/pending-installments/${userId}`);
                this.pendingInstallments = res.data.data.pending_installments || [];
                return this.pendingInstallments;
            } catch (err) {
                this.notify('Erro ao carregar parcelas pendentes!', 'error');
                return [];
            }
        },

        async openLinkModal(duplicate, payment) {
            this.selectedUser = {
                id: duplicate.user_id,
                name: duplicate.user_name,
                cpf: duplicate.user_cpf,
            };
            this.selectedPayment = payment;
            
            // Carregar parcelas pendentes do usuário
            await this.loadPendingInstallments(duplicate.user_id);
            
            this.showLinkModal = true;
        },

        closeLinkModal() {
            this.showLinkModal = false;
            this.selectedUser = null;
            this.selectedPayment = null;
            this.selectedInstallments = [];
            this.pendingInstallments = [];
        },

        linkPaymentToInstallment() {
            if (!this.selectedPayment || this.selectedInstallments.length === 0) {
                this.notify('Selecione pelo menos uma parcela para vincular!', 'warning');
                return;
            }

            this.loader = true;

            this.$axios.post('/payments/link-installment', {
                payment_id: this.selectedPayment.id,
                installment_ids: this.selectedInstallments // Enviando array
            })
            .then(res => {
                this.notify('Pagamento vinculado às parcelas com sucesso!', 'success');
                
                // Remover o pagamento da lista local
                this.removePaymentFromList(this.selectedPayment.id);
                
                this.closeLinkModal();
                this.loader = false;
            })
            .catch(err => {
                this.notify(err.response?.data?.message || 'Erro ao vincular pagamento!', 'error');
                this.loader = false;
            });
        },

        skipPayment(duplicate, payment) {
            // Remove o pagamento da lista sem vincular
            this.removePaymentFromList(payment.id);
            this.notify('Pagamento removido da lista!', 'info');
        },

        removePaymentFromList(paymentId) {
            // Percorre os duplicados e remove o pagamento específico
            this.duplicates = this.duplicates.map(duplicate => {
                // Filtra os pagamentos, removendo o que foi vinculado ou ignorado
                const filteredPayments = duplicate.payments.filter(p => p.id !== paymentId);
                
                return {
                    ...duplicate,
                    payments: filteredPayments,
                    payment_count: filteredPayments.length
                };
            }).filter(duplicate => duplicate.payments.length > 0); // Remove duplicados sem pagamentos
        },

        unlinkPayment(paymentId) {
            if (!confirm('Deseja realmente desvincular este pagamento da parcela?')) {
                return;
            }

            this.loader = true;

            this.$axios.delete(`/payments/unlink-installment/${paymentId}`)
            .then(res => {
                this.notify('Pagamento desvinculado com sucesso!', 'success');
                this.loadDuplicatePayments(); // Recarregar lista
                this.loader = false;
            })
            .catch(err => {
                this.notify(err.response?.data?.message || 'Erro ao desvincular pagamento!', 'error');
                this.loader = false;
            });
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR');
        },

        getMonthName(monthNumber) {
            const months = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                          'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
            return months[monthNumber - 1] || '';
        },

        formatCurrency(value) {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(value);
        },

        getTotalSelectedInstallments() {
            if (this.selectedInstallments.length === 0) return 0;
            
            return this.pendingInstallments
                .filter(inst => this.selectedInstallments.includes(inst.id))
                .reduce((sum, inst) => sum + parseFloat(inst.amount), 0);
        },

        getDistributionInfo() {
            if (!this.selectedPayment || this.selectedInstallments.length === 0) {
                return null;
            }

            const paymentValue = parseFloat(this.selectedPayment.credit_value);
            const totalInstallments = this.getTotalSelectedInstallments();
            const difference = paymentValue - totalInstallments;

            return {
                paymentValue,
                totalInstallments,
                difference,
                valid: difference >= 0 // Pagamento deve cobrir as parcelas
            };
        }
    },

    computed: {
        totalSelected() {
            return this.getTotalSelectedInstallments();
        },
        distributionInfo() {
            return this.getDistributionInfo();
        }
    }
}
</script>

<template>
    <section>
        <Loader v-if="loader" />

        <Head title="Gerenciar Vinculação de Pagamentos" />

        <div class="manage-installments-container">
            <div class="header-section">
                <h2>Pagamentos Duplicados no Mesmo Mês</h2>
                <p class="subtitle">Vincule manualmente cada pagamento à parcela correta quando houver múltiplos pagamentos no mesmo mês</p>
                <Button type="secondary" placeholder="Atualizar Lista" @buttonPressed="loadDuplicatePayments" />
            </div>

            <div v-if="duplicates.length === 0 && !loader" class="no-duplicates">
                <p>✅ Não há pagamentos duplicados para gerenciar no momento.</p>
            </div>

            <div v-else class="duplicates-list">
                <div v-for="duplicate in duplicates" :key="`${duplicate.user_id}-${duplicate.year}-${duplicate.month}`" class="duplicate-card">
                    <div class="user-info">
                        <h3>{{ duplicate.user_name }}</h3>
                        <p class="cpf">CPF: {{ duplicate.user_cpf }}</p>
                        <p class="period">
                            <strong>Período:</strong> {{ getMonthName(duplicate.month) }}/{{ duplicate.year }}
                            <span class="badge">{{ duplicate.payment_count }} pagamentos</span>
                        </p>
                    </div>

                    <div class="payments-grid">
                        <div v-for="(payment, index) in duplicate.payments" :key="payment.id" class="payment-item">
                            <div class="payment-header">
                                <span class="payment-number">Pagamento #{{ index + 1 }}</span>
                                <span v-if="payment.installment_id" class="linked-badge">✓ Vinculado</span>
                                <span v-else class="unlinked-badge">⚠ Não vinculado</span>
                            </div>
                            
                            <div class="payment-details">
                                <p><strong>Data:</strong> {{ formatDate(payment.payment_date) }}</p>
                                <p><strong>Valor:</strong> {{ formatCurrency(payment.credit_value) }}</p>
                                <p v-if="payment.installment_id">
                                    <strong>Parcela ID:</strong> {{ payment.installment_id }}
                                </p>
                            </div>

                            <div class="payment-actions">
                                <Button 
                                    v-if="!payment.installment_id"
                                    type="primary" 
                                    placeholder="Vincular à Parcela" 
                                    @buttonPressed="openLinkModal(duplicate, payment)" 
                                />
                                <Button 
                                    v-if="!payment.installment_id"
                                    type="tertiary" 
                                    placeholder="Não Vincular" 
                                    @buttonPressed="skipPayment(duplicate, payment)" 
                                />
                                <Button 
                                    v-else
                                    type="secondary" 
                                    placeholder="Desvincular" 
                                    @buttonPressed="unlinkPayment(payment.id)" 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal para vincular pagamento -->
        <section v-if="showLinkModal" class="modal-overlay" @click.self="closeLinkModal">
            <section class="modal-content">
                <div class="modal-header">
                    <h3>Vincular Pagamento à Parcela</h3>
                    <button class="close-btn" @click="closeLinkModal">×</button>
                </div>

                <div class="modal-body">
                    <div class="selected-info">
                        <h4>Associado</h4>
                        <p>{{ selectedUser?.name }}</p>
                        <p class="small">CPF: {{ selectedUser?.cpf }}</p>
                    </div>

                    <div class="selected-info">
                        <h4>Pagamento Selecionado</h4>
                        <p><strong>Data:</strong> {{ formatDate(selectedPayment?.payment_date) }}</p>
                        <p><strong>Valor:</strong> {{ formatCurrency(selectedPayment?.credit_value) }}</p>
                    </div>

                    <div class="installments-section">
                        <h4>Selecione as Parcelas (múltipla seleção)</h4>
                        <div v-if="pendingInstallments.length === 0" class="no-installments">
                            <p>Nenhuma parcela pendente disponível para este associado.</p>
                        </div>
                        <div v-else class="installments-grid">
                            <label 
                                v-for="installment in pendingInstallments" 
                                :key="installment.id" 
                                class="installment-option"
                                :class="{ 'selected': selectedInstallments.includes(installment.id) }"
                            >
                                <input 
                                    type="checkbox" 
                                    :value="installment.id" 
                                    v-model="selectedInstallments"
                                />
                                <div class="installment-details">
                                    <span class="month-year">{{ getMonthName(installment.month) }}/{{ installment.year }}</span>
                                    <span class="amount">{{ formatCurrency(installment.amount) }}</span>
                                    <span class="due-date">Venc: {{ formatDate(installment.due_date) }}</span>
                                    <span class="status" :class="installment.status">{{ installment.status }}</span>
                                </div>
                            </label>
                        </div>

                        <!-- Informações de Distribuição -->
                        <div v-if="selectedInstallments.length > 0" class="distribution-info">
                            <div class="info-row">
                                <span>Parcelas selecionadas:</span>
                                <strong>{{ selectedInstallments.length }}</strong>
                            </div>
                            <div class="info-row">
                                <span>Total das parcelas:</span>
                                <strong>{{ formatCurrency(totalSelected) }}</strong>
                            </div>
                            <div class="info-row">
                                <span>Valor do pagamento:</span>
                                <strong>{{ formatCurrency(distributionInfo?.paymentValue || 0) }}</strong>
                            </div>
                            <div class="info-row" :class="{ 'error': distributionInfo && !distributionInfo.valid }">
                                <span>Diferença:</span>
                                <strong>{{ formatCurrency(distributionInfo?.difference || 0) }}</strong>
                            </div>
                            <div v-if="distributionInfo && !distributionInfo.valid" class="warning-message">
                                ⚠️ O valor do pagamento é menor que o total das parcelas selecionadas!
                            </div>
                            <div v-else-if="distributionInfo && distributionInfo.difference > 0" class="info-message">
                                ℹ️ O valor do pagamento será distribuído proporcionalmente entre as {{ selectedInstallments.length }} parcelas selecionadas.
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-footer">
                    <Button type="secondary" placeholder="Cancelar" @buttonPressed="closeLinkModal" />
                    <Button type="primary" placeholder="Confirmar Vinculação" @buttonPressed="linkPaymentToInstallment" />
                </div>
            </section>
        </section>
    </section>
</template>

<style scoped>
.manage-installments-container {
    padding: 20px;
    padding-bottom: 120px;
    max-width: 1400px;
    margin: 0 auto;
    min-height: 100vh;
}

.header-section {
    background: white;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.header-section h2 {
    color: #333;
    margin-bottom: 10px;
}

.subtitle {
    color: #666;
    margin-bottom: 15px;
    font-size: 14px;
}

.no-duplicates {
    background: #e8f5e9;
    padding: 40px;
    border-radius: 8px;
    text-align: center;
    color: #2e7d32;
    font-size: 16px;
}

.duplicates-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.duplicate-card {
    background: white;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.user-info {
    border-bottom: 2px solid #f0f0f0;
    padding-bottom: 15px;
    margin-bottom: 20px;
}

.user-info h3 {
    color: #333;
    margin-bottom: 5px;
}

.cpf {
    color: #666;
    font-size: 14px;
    margin-bottom: 5px;
}

.period {
    color: #333;
    font-size: 14px;
}

.badge {
    background: #ff9800;
    color: white;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    margin-left: 10px;
}

.payments-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 15px;
}

.payment-item {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 15px;
    background: #fafafa;
}

.payment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.payment-number {
    font-weight: bold;
    color: #333;
}

.linked-badge {
    background: #4caf50;
    color: white;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
}

.unlinked-badge {
    background: #ff5722;
    color: white;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
}

.payment-details {
    margin-bottom: 15px;
}

.payment-details p {
    margin: 5px 0;
    font-size: 14px;
    color: #555;
}

.payment-actions {
    display: flex;
    gap: 10px;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 700px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 20px rgba(0,0,0,0.3);
}

.modal-header {
    padding: 20px;
    border-bottom: 1px solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-shrink: 0;
}

.modal-header h3 {
    margin: 0;
    color: #333;
}

.close-btn {
    background: none;
    border: none;
    font-size: 32px;
    cursor: pointer;
    color: #999;
    line-height: 1;
}

.close-btn:hover {
    color: #333;
}

.modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
}

.selected-info {
    background: #f5f5f5;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 12px;
}

.selected-info h4 {
    margin: 0 0 8px 0;
    color: #555;
    font-size: 13px;
    text-transform: uppercase;
}

.selected-info p {
    margin: 3px 0;
    color: #333;
    font-size: 14px;
}

.selected-info .small {
    font-size: 13px;
    color: #666;
}

.installments-section {
    margin-top: 15px;
}

.installments-section h4 {
    margin-bottom: 10px;
    color: #333;
    font-size: 15px;
}

.no-installments {
    padding: 20px;
    text-align: center;
    color: #999;
    background: #f9f9f9;
    border-radius: 8px;
}

.installments-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 250px;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 10px;
    background: #fafafa;
}

.installment-option {
    display: flex;
    align-items: center;
    padding: 12px;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
}

.installment-option:hover {
    border-color: #2196f3;
    background: #f0f7ff;
}

.installment-option.selected {
    border-color: #2196f3;
    background: #e3f2fd;
}

.installment-option input[type="radio"],
.installment-option input[type="checkbox"] {
    margin-right: 12px;
    cursor: pointer;
    width: 18px;
    height: 18px;
}

.installment-details {
    display: flex;
    gap: 15px;
    align-items: center;
    flex: 1;
}

.month-year {
    font-weight: bold;
    color: #333;
    min-width: 120px;
}

.amount {
    color: #2196f3;
    font-weight: bold;
    min-width: 100px;
}

.due-date {
    color: #666;
    font-size: 13px;
    min-width: 120px;
}

.status {
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    text-transform: capitalize;
}

.status.pending {
    background: #fff3cd;
    color: #856404;
}

.status.overdue {
    background: #f8d7da;
    color: #721c24;
}

.status.paid {
    background: #d4edda;
    color: #155724;
}

.distribution-info {
    margin-top: 15px;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #2196f3;
}

.distribution-info .info-row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    border-bottom: 1px solid #e0e0e0;
    font-size: 14px;
}

.distribution-info .info-row:last-of-type {
    border-bottom: none;
}

.distribution-info .info-row.error {
    color: #d32f2f;
    font-weight: bold;
}

.distribution-info .warning-message {
    margin-top: 8px;
    padding: 8px;
    background: #fff3cd;
    color: #856404;
    border-radius: 6px;
    font-size: 13px;
    text-align: center;
}

.distribution-info .info-message {
    margin-top: 8px;
    padding: 8px;
    background: #d1ecf1;
    color: #0c5460;
    border-radius: 6px;
    font-size: 13px;
    text-align: center;
}

.modal-footer {
    padding: 20px;
    border-top: 1px solid #e0e0e0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
    background: white;
}
</style>
